import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import "./Navigation.css";

import {
  attemptNavigationWithGuard,
  getAccountLabel,
  getAuthenticatedMenuItems,
  getAuthenticatedTabs,
  getBrandTarget,
  getPublicLinks,
  getUserDisplayName,
  isActivePath,
  isModifiedNavigationEvent,
  useNavigationScrollState,
} from "./navigation.functions";
import BrandLogo from "../primitives/brand-logo";

function getReadableTextColor(hexColor) {
  const hex = String(hexColor || "").replace("#", "");
  if (hex.length !== 6) return "#ffffff";
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 145 ? "#111827" : "#ffffff";
}

function NavigationAvatar({
  showAvatar,
  userAvatarUrl,
  userAvatarColor = "",
  displayName,
  onError,
  className = "authNav__avatar",
}) {
  const fallbackLetter = (displayName?.[0] || "U").toUpperCase();
  const fallbackStyle =
    /^#[0-9a-fA-F]{6}$/.test(String(userAvatarColor || "").trim())
      ? {
          backgroundColor: String(userAvatarColor).trim(),
          backgroundImage: "none",
          color: getReadableTextColor(userAvatarColor),
        }
      : undefined;

  if (showAvatar) {
    return <img src={userAvatarUrl} alt="Profile" className={className} onError={onError} />;
  }

  return (
    <div className={`${className} authNav__avatar--fallback`} style={fallbackStyle}>
      {fallbackLetter}
    </div>
  );
}

export default function Navigation({
  isAuthenticated,
  userEmail,
  userName,
  userAvatarUrl,
  userAvatarColor = "",
  hidePrimaryTabs = false,
  onSignOut,
  hasActiveNotifications = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [authMobileMenuTop, setAuthMobileMenuTop] = useState(null);
  const menuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const authMobileToggleRef = useRef(null);
  const authMobileHeaderRef = useRef(null);
  const publicMobileToggleRef = useRef(null);
  const isScrolled = useNavigationScrollState(location.pathname);
  const brandTarget = getBrandTarget(isAuthenticated);

  const handleGuardedNavigate = ({ to, afterNavigate }) =>
    attemptNavigationWithGuard({
      from: location.pathname,
      to,
      proceed: () => {
        navigate(to);
        if (typeof afterNavigate === "function") afterNavigate();
      },
    });

  const handleGuardedAction = ({ to = "", action, afterAction }) =>
    attemptNavigationWithGuard({
      from: location.pathname,
      to,
      proceed: () => {
        if (typeof action === "function") action();
        if (typeof afterAction === "function") afterAction();
      },
    });

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [userAvatarUrl]);

  useEffect(() => {
    setMenuOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event) {
      const target = event.target;
      if (
        (authMobileToggleRef.current && authMobileToggleRef.current.contains(target)) ||
        (publicMobileToggleRef.current && publicMobileToggleRef.current.contains(target))
      ) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(target)) {
        setMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setMobileMenuOpen(false);
      }
    }
    if (menuOpen || mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [menuOpen, mobileMenuOpen]);

  useEffect(() => {
    function handleEscape(event) {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      setMobileMenuOpen(false);
    }

    if (menuOpen || mobileMenuOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => document.removeEventListener("keydown", handleEscape);
  }, [menuOpen, mobileMenuOpen]);

  useEffect(() => {
    if (!isAuthenticated || !mobileMenuOpen) {
      document.body.removeAttribute("data-auth-mobile-menu-open");
      return undefined;
    }

    document.body.setAttribute("data-auth-mobile-menu-open", "true");
    return () => document.body.removeAttribute("data-auth-mobile-menu-open");
  }, [isAuthenticated, mobileMenuOpen]);

  useEffect(() => {
    if (!isAuthenticated || !mobileMenuOpen) {
      setAuthMobileMenuTop(null);
      return undefined;
    }

    const updateAuthMobileMenuTop = () => {
      const headerBottom = authMobileHeaderRef.current?.getBoundingClientRect().bottom;
      setAuthMobileMenuTop(Number.isFinite(headerBottom) ? Math.max(0, Math.round(headerBottom)) : null);
    };

    updateAuthMobileMenuTop();
    window.addEventListener("resize", updateAuthMobileMenuTop);
    window.addEventListener("scroll", updateAuthMobileMenuTop, { passive: true });

    return () => {
      window.removeEventListener("resize", updateAuthMobileMenuTop);
      window.removeEventListener("scroll", updateAuthMobileMenuTop);
    };
  }, [isAuthenticated, mobileMenuOpen]);

  // -----------------------------
  // AUTHENTICATED (auth-nav style)
  // -----------------------------
  if (isAuthenticated) {
    const displayName = getUserDisplayName(userName, userEmail);
    const showAvatar = Boolean(userAvatarUrl) && !avatarLoadFailed;
    const accountLabel = getAccountLabel(displayName);
    const tabs = getAuthenticatedTabs({ hasActiveNotifications });
    const accountMenuItems = getAuthenticatedMenuItems(location.pathname);

    return (
      <header className={`authNav ${isScrolled ? "authNav--scrolled" : ""}`}>
        <div className="authNav__container">
          {/* Mobile header */}
          <div className="authNav__mobileHeader" ref={authMobileHeaderRef}>
            <Link
              to={brandTarget}
              className="authNav__mobileLogo"
              aria-label="Home"
              onClick={(event) => {
                if (isModifiedNavigationEvent(event)) return;
                event.preventDefault();
                handleGuardedNavigate({
                  to: brandTarget,
                  afterNavigate: () => setMobileMenuOpen(false),
                });
              }}
            >
              <BrandLogo className="authNav__logo authNav__logo--mobile" />
            </Link>

            <button
              ref={authMobileToggleRef}
              type="button"
              className="authNav__mobileHamburger"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-expanded={mobileMenuOpen}
              aria-haspopup="true"
              aria-controls="auth-mobile-menu"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <span className="authNav__hamburgerLine" />
              <span className="authNav__hamburgerLine" />
              <span className="authNav__hamburgerLine" />
            </button>
          </div>

          {mobileMenuOpen && (
            <div
              id="auth-mobile-menu"
              className="authNav__mobileMenu"
              ref={mobileMenuRef}
              style={
                authMobileMenuTop == null
                  ? undefined
                  : { "--auth-nav-mobile-menu-top": `${authMobileMenuTop}px` }
              }
            >
              {/* Mobile menu */}
              <div className="authNav__mobileProfile">
                <NavigationAvatar
                  showAvatar={showAvatar}
                  userAvatarUrl={userAvatarUrl}
                  userAvatarColor={userAvatarColor}
                  displayName={displayName}
                  onError={() => setAvatarLoadFailed(true)}
                />
                <div className="authNav__profileMeta">
                  <div className="authNav__name">{displayName}</div>
                  <Link
                    to="/edit-profile"
                    className="authNav__edit"
                    onClick={(event) => {
                      if (isModifiedNavigationEvent(event)) return;
                      event.preventDefault();
                      handleGuardedNavigate({
                        to: "/edit-profile",
                        afterNavigate: () => setMobileMenuOpen(false),
                      });
                    }}
                  >
                    <User className="authNav__editIcon" aria-hidden="true" />
                    <span>Update Profile</span>
                  </Link>
                </div>
              </div>

              {!hidePrimaryTabs && (
                <div className="authNav__mobileTabs">
                  {tabs.map((t) => {
                    const active = isActivePath(location.pathname, t.path);

                    return (
                      <div key={t.id} className="authNav__mobileTabItem">
                        <button
                          type="button"
                          className={`authNav__mobileTab ${active ? "authNav__mobileTab--active" : ""}`}
                          onClick={() => {
                            if (active) return;
                            handleGuardedNavigate({
                              to: t.path,
                              afterNavigate: () => setMobileMenuOpen(false),
                            });
                          }}
                          {...(t.walkthroughId ? { "data-walkthrough": t.walkthroughId } : {})}
                        >
                          <span className="authNav__tabLabel">
                            {t.label}
                            {t.hasNotification && <span className="authNav__notificationDot" />}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="authNav__mobileMenuItems">
                {accountMenuItems.map((item) => (
                  <Link
                    key={`mobile-${item.id}`}
                    to={item.path}
                    className={`authNav__mobileMenuItem ${item.isActive ? "authNav__mobileMenuItem--active" : ""}`}
                    onClick={(event) => {
                      if (isModifiedNavigationEvent(event)) return;
                      event.preventDefault();
                      handleGuardedNavigate({
                        to: item.path,
                        afterNavigate: () => setMobileMenuOpen(false),
                      });
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
                {typeof onSignOut === "function" && (
                  <button
                    type="button"
                    className="authNav__mobileMenuItem authNav__mobileMenuItem--button"
                    onClick={() => {
                      handleGuardedAction({
                        to: "__sign_out__",
                        action: onSignOut,
                        afterAction: () => setMobileMenuOpen(false),
                      });
                    }}
                  >
                    Sign out
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Desktop shell */}
          <div className="authNav__desktopShell">
            <Link
              to={brandTarget}
              className="authNav__logoLink authNav__logoLink--desktop"
              aria-label="Home"
              onClick={(event) => {
                if (isModifiedNavigationEvent(event)) return;
                event.preventDefault();
                handleGuardedNavigate({ to: brandTarget });
              }}
            >
              <BrandLogo className="authNav__logo authNav__logo--desktop" />
            </Link>

            <div className="authNav__desktopActions">
              {!hidePrimaryTabs && (
                <nav className="authNav__tabs authNav__tabs--desktop" aria-label="Primary">
                  {tabs.map((t) => {
                    const active = isActivePath(location.pathname, t.path);

                    return (
                      <div key={t.id} className="authNav__tabItem">
                        <button
                          type="button"
                          className={`authNav__tab ${active ? "authNav__tab--active" : ""}`}
                          onClick={() => {
                            if (active) return;
                            handleGuardedNavigate({ to: t.path });
                          }}
                          {...(t.walkthroughId ? { "data-walkthrough": t.walkthroughId } : {})}
                        >
                          <span className="authNav__tabLabel">
                            {t.label}
                            {t.hasNotification && <span className="authNav__notificationDot" />}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </nav>
              )}

              <div className="authNav__right" ref={menuRef}>
                <button
                  type="button"
                  className="authNav__accountButton"
                  onClick={() => setMenuOpen((prev) => !prev)}
                  aria-expanded={menuOpen}
                  aria-haspopup="true"
                  aria-label="Open account menu"
                >
                  <NavigationAvatar
                    showAvatar={showAvatar}
                    userAvatarUrl={userAvatarUrl}
                    userAvatarColor={userAvatarColor}
                    displayName={displayName}
                    onError={() => setAvatarLoadFailed(true)}
                  />
                  <span className="authNav__accountName">{accountLabel}</span>
                  <span className={`authNav__accountChevron ${menuOpen ? "authNav__accountChevron--open" : ""}`} aria-hidden="true" />
                </button>
                {menuOpen && (
                  <div className="authNav__dropdown">
                    {accountMenuItems.map((item) => (
                      <Link
                        key={`desktop-${item.id}`}
                        to={item.path}
                        className={`authNav__dropdownItem ${item.isActive ? "authNav__dropdownItem--active" : ""}`}
                        onClick={(event) => {
                          if (isModifiedNavigationEvent(event)) return;
                          event.preventDefault();
                          handleGuardedNavigate({
                            to: item.path,
                            afterNavigate: () => setMenuOpen(false),
                          });
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                    {typeof onSignOut === "function" && (
                      <button
                        type="button"
                        className="authNav__dropdownItem authNav__dropdownItem--button"
                        onClick={() => {
                          handleGuardedAction({
                            to: "__sign_out__",
                            action: onSignOut,
                            afterAction: () => setMenuOpen(false),
                          });
                        }}
                      >
                        Sign out
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  // -----------------------------
  // PUBLIC (Landing nav styling)
  // -----------------------------
  const navLinks = getPublicLinks();

  return (
    <header className={`publicNav ${isScrolled ? "publicNav--scrolled" : ""}`}>
      <div className="publicNav__inner">
        {/* Brand */}
        <Link to={brandTarget} className="publicNav__logo" aria-label="Home">
          <BrandLogo className="publicNav__logoImage" />
        </Link>

        {/* Mobile toggle */}
        <button
          ref={publicMobileToggleRef}
          type="button"
          className="publicNav__hamburger"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-expanded={mobileMenuOpen}
          aria-haspopup="true"
          aria-controls="public-mobile-menu"
          aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
        >
          <span className="publicNav__hamburgerLine" />
          <span className="publicNav__hamburgerLine" />
          <span className="publicNav__hamburgerLine" />
        </button>

        {/* Desktop links */}
        <nav className="publicNav__links" aria-label="Primary">
          {navLinks.map((link) => {
            const active = isActivePath(location.pathname, link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`publicNav__link ${active ? "publicNav__link--active" : ""}`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {mobileMenuOpen && (
          <nav id="public-mobile-menu" className="publicNav__mobileMenu" ref={mobileMenuRef} aria-label="Primary">
            {/* Mobile links */}
            {navLinks.map((link) => {
              const active = isActivePath(location.pathname, link.path);
              return (
                <Link
                  key={`${link.path}-mobile`}
                  to={link.path}
                  className={`publicNav__mobileLink ${active ? "publicNav__mobileLink--active" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
        )}
      </div>
    </header>
  );
}
